import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import ElitureDashboard from './components/ElitureDashboard';
import ElitureChat from './components/ElitureChat';

import api from './api';

// ── Login / Register Page ─────────────────────────────────────────────────────
function LoginPage({ onLogin }) {
  const [mode, setMode] = useState('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'register') {
        await api.post('/auth/register', { email, password, name });
      }
      const res = await api.post('/auth/login', { email, password });
      onLogin(res.data.user, res.data.token);
    } catch (err) {
      setError(err.response?.data?.error || 'Authentication failed. Check your credentials and try again.');
    }
    setLoading(false);
  };

  const handleDemo = () => {
    // Demo mode: admin role so all features are visible
    const demoUser = { id: 'demo-user-id', name: 'Alex Johnson', email: 'alex@eliture.com', role: 'admin' };
    onLogin(demoUser, 'demo-token');
  };

  const inputStyle = {
    width: '100%', background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(99,102,241,0.25)', borderRadius: 10,
    padding: '12px 16px', color: '#fff', fontSize: '0.9rem',
    outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
  };
  const labelStyle = {
    display: 'block', fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)',
    textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6, fontWeight: 600,
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        {/* Logo / branding */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{
            width: 60, height: 60, margin: '0 auto 16px',
            background: 'linear-gradient(135deg, #6366f1, #10b981)', borderRadius: 16,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 28, fontWeight: 900, color: '#fff', boxShadow: '0 0 30px rgba(99,102,241,0.4)',
          }}>E</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#fff', letterSpacing: '-0.5px' }}>
            Eliture <span style={{ color: '#6366f1' }}>LMS</span>
          </div>
          <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
            {mode === 'login' ? 'Sign in to continue learning' : 'Create your learner account'}
          </div>
        </div>

        {/* Card */}
        <div style={{
          background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(99,102,241,0.2)',
          borderRadius: 20, padding: '32px 28px', backdropFilter: 'blur(20px)',
        }}>
          {/* Mode tabs */}
          <div style={{ display: 'flex', background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: 4, marginBottom: 28 }}>
            {['login', 'register'].map(m => (
              <button key={m} onClick={() => { setMode(m); setError(''); }} style={{
                flex: 1, padding: 9, borderRadius: 8, border: 'none',
                background: mode === m ? 'linear-gradient(135deg,rgba(99,102,241,0.4),rgba(16,185,129,0.25))' : 'transparent',
                color: mode === m ? '#fff' : 'rgba(255,255,255,0.4)',
                fontWeight: mode === m ? 700 : 400, cursor: 'pointer',
                fontSize: '0.85rem', fontFamily: 'inherit',
              }}>
                {m === 'login' ? 'Sign In' : 'Register'}
              </button>
            ))}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {mode === 'register' && (
              <div>
                <label style={labelStyle}>Full Name</label>
                <input value={name} onChange={e => setName(e.target.value)}
                  placeholder="Your full name" required style={inputStyle}
                  onFocus={e => e.target.style.borderColor = 'rgba(99,102,241,0.6)'}
                  onBlur={e => e.target.style.borderColor = 'rgba(99,102,241,0.25)'} />
              </div>
            )}
            <div>
              <label style={labelStyle}>Email Address</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com" required style={inputStyle}
                onFocus={e => e.target.style.borderColor = 'rgba(99,102,241,0.6)'}
                onBlur={e => e.target.style.borderColor = 'rgba(99,102,241,0.25)'} />
            </div>
            <div>
              <label style={labelStyle}>Password</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                placeholder={mode === 'register' ? 'Min. 8 characters' : '••••••••'} required style={inputStyle}
                onFocus={e => e.target.style.borderColor = 'rgba(99,102,241,0.6)'}
                onBlur={e => e.target.style.borderColor = 'rgba(99,102,241,0.25)'} />
            </div>

            {error && (
              <div style={{
                background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                borderRadius: 10, padding: '12px 14px', color: '#f87171', fontSize: '0.82rem',
              }}>⚠️ {error}</div>
            )}

            <button type="submit" disabled={loading} style={{
              background: loading ? 'rgba(99,102,241,0.4)' : 'linear-gradient(135deg,#6366f1,#10b981)',
              border: 'none', borderRadius: 12, padding: 13, color: '#fff',
              fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '0.95rem', fontFamily: 'inherit',
              boxShadow: loading ? 'none' : '0 4px 20px rgba(99,102,241,0.4)',
            }}>
              {loading ? 'Please wait…' : mode === 'login' ? 'Sign In →' : 'Create Account →'}
            </button>
          </form>

          {/* Divider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '22px 0' }}>
            <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' }} />
            <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)', letterSpacing: 1 }}>OR</span>
            <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' }} />
          </div>

          {/* Demo button */}
          <button onClick={handleDemo} style={{
            width: '100%', background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.12)', borderRadius: 12, padding: 12,
            color: 'rgba(255,255,255,0.75)', fontWeight: 600, cursor: 'pointer',
            fontSize: '0.875rem', fontFamily: 'inherit',
          }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
          >
            🚀 Continue in Demo Mode
          </button>
          <div style={{ textAlign: 'center', marginTop: 8, fontSize: '0.68rem', color: 'rgba(255,255,255,0.22)' }}>
            Demo uses sample data. No account required.
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────
function App() {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Restore session from localStorage on mount
  useEffect(() => {
    const token = localStorage.getItem('eliture_token');
    const storedUser = localStorage.getItem('eliture_user');
    if (token && storedUser) {
      try {
        setUser(JSON.parse(storedUser));
        setIsAuthenticated(true);
      } catch {
        localStorage.removeItem('eliture_token');
        localStorage.removeItem('eliture_user');
      }
    }
    setIsLoading(false);
  }, []);

  const handleLogin = (userData, token) => {
    localStorage.setItem('eliture_token', token);
    localStorage.setItem('eliture_user', JSON.stringify(userData));
    setUser(userData);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('eliture_token');
    localStorage.removeItem('eliture_user');
    setUser(null);
    setIsAuthenticated(false);
  };

  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 60, height: 60, margin: '0 auto 20px',
            background: 'linear-gradient(135deg, #6366f1, #10b981)', borderRadius: 15,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 28, fontWeight: 900, color: '#fff', animation: 'pulse 2s ease-in-out infinite',
          }}>E</div>
          <div style={{ color: '#fff', fontSize: '1.1rem', fontWeight: 600 }}>Loading Eliture LMS…</div>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)',
        fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
        position: 'relative', overflow: 'hidden',
      }}>
        {/* Ambient background lights */}
        <div style={{
          position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0,
          background: `
            radial-gradient(ellipse at 20% 20%, rgba(99,102,241,0.15) 0%, transparent 50%),
            radial-gradient(ellipse at 80% 80%, rgba(16,185,129,0.10) 0%, transparent 50%),
            radial-gradient(ellipse at 60% 10%, rgba(245,158,11,0.08) 0%, transparent 40%)
          `,
        }} />

        {/* Navbar — authenticated only */}
        {isAuthenticated && user && (
          <nav style={{
            position: 'sticky', top: 0, zIndex: 100,
            background: 'rgba(15,12,41,0.85)', backdropFilter: 'blur(20px)',
            borderBottom: '1px solid rgba(99,102,241,0.2)',
            height: 68, display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', padding: '0 32px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 40, height: 40, background: 'linear-gradient(135deg, #6366f1, #10b981)',
                borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 20, fontWeight: 900, color: '#fff', boxShadow: '0 0 20px rgba(99,102,241,0.4)',
              }}>E</div>
              <div>
                <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#fff', letterSpacing: '-0.5px' }}>
                  Eliture <span style={{ color: '#6366f1' }}>LMS</span>
                </div>
                <div style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.35)', letterSpacing: 1.5, textTransform: 'uppercase' }}>
                  Learning Management System
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              {user.id === 'demo-user-id' && (
                <div style={{
                  fontSize: '0.68rem', fontWeight: 700, color: '#f59e0b',
                  background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.25)',
                  padding: '4px 10px', borderRadius: 20, letterSpacing: 0.5,
                }}>DEMO</div>
              )}
              <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: 1 }}>
                {user.role}
              </div>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.2)',
                padding: '7px 14px', borderRadius: 40,
              }}>
                <div style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #6366f1, #10b981)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontSize: 13, fontWeight: 700,
                }}>
                  {(user.name || 'U').charAt(0).toUpperCase()}
                </div>
                <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.875rem', fontWeight: 500 }}>
                  {user.name}
                </span>
              </div>
              <button onClick={handleLogout} style={{
                background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                color: 'rgba(255,255,255,0.8)', padding: '8px 16px',
                borderRadius: 8, fontSize: '0.875rem', fontWeight: 500,
                cursor: 'pointer', transition: 'all 0.2s', fontFamily: 'inherit',
              }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.2)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(239,68,68,0.1)'}
              >
                Logout
              </button>
            </div>
          </nav>
        )}

        {/* Routes */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          <Routes>
            <Route
              path="/"
              element={isAuthenticated ? <ElitureDashboard user={user} /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/login"
              element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage onLogin={handleLogin} />}
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>

        {/* Floating AI chat */}
        {isAuthenticated && user && <ElitureChat user={user} />}
      </div>
    </Router>
  );
}

export default App;
